"use client";
import { api } from "@/trpc/react";
import { signOut } from "next-auth/react";
import React, {
  Dispatch,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { toast } from "react-toastify";
import { Subscription } from "@/schemas/subscription";

function isBenignDeleteSubscriptionError(message: string) {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("failed to delete subscription") ||
    normalized.includes("not found") ||
    normalized.includes("does not exist") ||
    normalized.includes("already deleted") ||
    normalized.includes("already removed")
  );
}

export type AccountDispatcherActions =
  | { type: "first-name"; firstName: string }
  | { type: "last-name"; lastName: string }
  | { type: "email"; email: string }
  | { type: "company"; company: string }
  | { type: "profession"; profession: string }
  | { type: "id"; id: string }
  | {
      type: "init";
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      company: string;
      profession: string;
    }
  | { type: "toggle-prompt" };

export type AccountContextState = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profession: string;
  company: string;
  open: boolean;
};

export type AccountHook = {
  state: AccountContextState;
  dispatch: Dispatch<AccountDispatcherActions>;
  deleteAccount: (
    username: string,
    email: string,
    subscription: Subscription | null,
  ) => Promise<void>;
};

const initialState: AccountContextState = {
  id: "0",
  firstName: "",
  lastName: "",
  email: "",
  profession: "",
  company: "",
  open: false,
};

export const AccountContext = createContext<AccountHook | null>(null);
const accountReducer = (
  current: AccountContextState,
  action: AccountDispatcherActions,
) => {
  const copy = { ...current };
  switch (action.type) {
    case "first-name":
      copy.firstName = action.firstName;
      break;
    case "last-name":
      copy.lastName = action.lastName;
      break;
    case "email":
      copy.email = action.email;
      break;
    case "profession":
      copy.profession = action.profession;
      break;
    case "company":
      copy.company = action.company;
      break;
    case "id":
      copy.id = action.id;
      break;
    case "init":
      copy.id = action.id;
      copy.firstName = action.firstName;
      copy.lastName = action.lastName;
      copy.email = action.email;
      copy.company = action.company;
      copy.profession = action.profession;
      break;
    case "toggle-prompt":
      copy.open = !copy.open;
      break;
  }
  return copy;
};

export function AccountContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const { mutateAsync: deletedAccountMutation } =
    api.account.deleteUser.useMutation();
  const { mutateAsync: sendDeletedAccountEmail } =
    api.messages.sendAccountDeletedEmail.useMutation();
  const { mutateAsync: cancelStripeSubscription } =
    api.stripe.cancelSubscription.useMutation();
  const { mutateAsync: deleteSubscriptionRecord } =
    api.subscribe.deleteSubscription.useMutation();
  const { mutateAsync: deleteStripeAccount } =
    api.stripe.deleteCustomer.useMutation();
  const { mutateAsync: deleteBCAccount } =
    api.bcaccount.deleteUser.useMutation();

  async function deleteAccount(
    _username: string,
    email: string,
    subscription: Subscription | null,
  ) {
    if (!subscription) {
      //verify we have a valid  record
      toast.error("Missing subscription record!");
      return;
    }
    const { subscription_id, customer_id } = subscription;

    //cancel subscription
    //if the user never subscribed or canceled, then skip this step
    if (!["canceled", "opted-out"].includes(subscription_id)) {
      const res = await cancelStripeSubscription();
      if (res.type === "error") {
        console.error(res.message);
        toast.error(res.message);
      }
    }

    //delete bc account
    {
      const deleteRes = await deleteBCAccount();
      if (deleteRes.type === "error") {
        toast.error(deleteRes.message);
      }
    }

    //delete stripe account if it ever created one
    if (customer_id !== "opted-out") {
      const res = await deleteStripeAccount();
      if (res.type === "error") {
        console.error(res.message);
        toast.error(res.message);
      }
    }

    //delete aws record
    {
      const res = await deleteSubscriptionRecord();
      if (res.type === "error") {
        if (isBenignDeleteSubscriptionError(res.message)) {
          console.info(
            "[INFO] benign subscription delete error: ",
            res.message,
          );
        } else {
          console.error(res.message);
          toast.error(res.message);
        }
      }
    }

    //delete cognito account
    {
      const res = await deletedAccountMutation();
      if (res.type === "error") {
        console.error(res.message);
        toast.error(res.message);
      }
      signOut({ callbackUrl: "/" });
    }

    //send email
    {
      const res = await sendDeletedAccountEmail({ recipient: email });
      if (res.type === "error") {
        console.error(res.message);
        toast.error(res.message);
      }
    }
  }

  return (
    <AccountContext.Provider value={{ state, dispatch, deleteAccount }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccountContext() {
  const context = useContext(AccountContext);
  if (context === null) {
    throw new Error("Missing account Context");
  }
  return context;
}
